/**
 * esimSelection.ts
 *
 * Encoding for the Quick Tests step-2 eSIM picker.
 *
 * The wizard holds ONE selection value that can mean three different things,
 * so the value carries a prefix that says which:
 *
 *   `<groupId>`                        → an org eSIM group; the allocator picks
 *                                        any free member at run time.
 *   `profile:<profileId>`              → one specific SIM in the org inventory.
 *   `shared:<sharedPoolSentinel(gid)>` → the super-admin shared pool for a
 *                                        granted group (see shared-types
 *                                        `sharedPoolSentinel`).
 *
 * Both prefixed forms resolve to something the run paths pass as
 * `eSimProfileId`; the bare form is passed as `eSimGroupId`. The `shared:`
 * prefix predates the `profile:` one — it is kept byte-compatible because the
 * selection is persisted to localStorage per org.
 */

import type { ESimProfile, ForcedRat } from '@simcheck/shared-types';
import {
  isProfileReinstallable,
  esimRelocationPolicy,
  effectiveDownloadsLeft,
  FORCED_RAT_LABELS,
} from '@simcheck/shared-types';

export const PROFILE_PREFIX = 'profile:';
export const SHARED_PREFIX = 'shared:';

export interface ParsedESimSelection {
  /** Org eSIM group — allocator picks any available member. */
  groupId: string | null;
  /**
   * Value to pass as `eSimProfileId`: either a real `esim_profiles` doc id or
   * a shared-pool sentinel the backend resolves to a pool member.
   */
  profileId: string | null;
  /** True when `profileId` is a shared-pool sentinel, not a real doc id. */
  isSharedPool: boolean;
}

const EMPTY: ParsedESimSelection = { groupId: null, profileId: null, isSharedPool: false };

/** Decode a persisted/selected value into the shape the run paths need. */
export function parseESimSelection(value: string | null | undefined): ParsedESimSelection {
  if (!value) return EMPTY;
  if (value.startsWith(SHARED_PREFIX)) {
    return { groupId: null, profileId: value.slice(SHARED_PREFIX.length), isSharedPool: true };
  }
  if (value.startsWith(PROFILE_PREFIX)) {
    return { groupId: null, profileId: value.slice(PROFILE_PREFIX.length), isSharedPool: false };
  }
  return { groupId: value, profileId: null, isSharedPool: false };
}

/** Encode a specific org profile as a selection value. */
export function encodeProfileSelection(profileId: string): string {
  return `${PROFILE_PREFIX}${profileId}`;
}

/**
 * True when the selection points at a concrete org profile whose details can
 * be fetched with `getProfile()`. Shared-pool sentinels are NOT doc ids, so
 * callers must not try to load them.
 */
export function isConcreteProfileSelection(value: string | null | undefined): boolean {
  return !!value && value.startsWith(PROFILE_PREFIX);
}

// ─── Specific-SIM placement ────────────────────────────────────────────────
//
// A SIM is not freely combinable with a location. It lives on exactly one
// eUICC at a time, and whether it can serve a run elsewhere depends on whether
// it can be re-downloaded. This mirrors `QueueService._resolveEsimPlacement`
// (apps/frontend-new/functions/src/services/QueueService.ts) so the wizard can
// say "this can't work" up front instead of letting the run hard-fail at
// dispatch. Keep the two in sync.

export type ESimPlacementStatus =
  /** Not installed anywhere yet — downloads onto whichever modem is allocated. */
  | 'installs_fresh'
  /** Resident at a selected location; runs in place on the holder modem. */
  | 'runs_in_place'
  /** Resident elsewhere but re-downloadable — the run relocates it. */
  | 'relocates'
  /** Resident at a selected location, but more locations are selected than it can serve. */
  | 'one_location_only'
  /** Cannot serve the selected location(s) at all — the run would hard-fail. */
  | 'blocked';

export interface ESimPlacement {
  status: ESimPlacementStatus;
  /** True when a run against the current selection is guaranteed to fail. */
  blocking: boolean;
  /** Location the SIM currently lives at, when known. */
  holderLocationId: string | null;
  holderImei: string | null;
  /** User-facing explanation; null when there's nothing worth saying. */
  message: string | null;
}

/**
 * Decide whether a specific SIM can serve the selected location(s).
 *
 * `deviceLocationOf` maps a holder deviceId → its locationId (null when the
 * device is unknown to the caller). `locationNameOf` renders ids for humans.
 */
export function resolveESimPlacement(args: {
  profile: ESimProfile;
  selectedLocationIds: string[];
  deviceLocationOf: (deviceId: string) => string | null;
  locationNameOf: (locationId: string | null) => string;
  /**
   * Optional holder-IMEI → modem model name lookup. When it resolves, the
   * model is shown in parentheses right after the IMEI in the message
   * ("modem 8673… (Quectel RM520N-GL)") so the tester knows what hardware the
   * pinned SIM runs on. Returns null when the model isn't known (e.g. the
   * holder isn't among the loaded modems) — the message falls back to the IMEI.
   */
  modemModelOf?: (imei: string) => string | null;
  /**
   * The run's forced network type and the holder modem's supported types
   * (null = unknown). When the holder can't do that type, the run cannot
   * stay in place — the backend moves the SIM to a modem here that can (#381:
   * the hint used to say "Runs in place" for exactly that run).
   */
  forcedRat?: ForcedRat;
  holderRatsOf?: (imei: string) => Set<string> | null;
}): ESimPlacement {
  const { profile, selectedLocationIds, deviceLocationOf, locationNameOf, modemModelOf, forcedRat, holderRatsOf } = args;
  // Render a holder IMEI with its modem model appended when known.
  const modemLabel = (imei: string) => {
    const model = modemModelOf?.(imei);
    return model ? `${imei} (${model})` : imei;
  };
  const io = profile.installedOn;
  const reinstallable = isProfileReinstallable(profile);
  const consumedLabel =
    profile.downloadLimit === 'single_use'
      ? 'This is a single-use eSIM and its code has already been used'
      : 'This eSIM has used up its download allowance';

  // Not installed anywhere the platform has seen.
  if (!io?.deviceId || !io?.imei) {
    if (profile.shared) {
      return {
        status: 'blocked',
        blocking: true,
        holderLocationId: null,
        holderImei: null,
        message:
          'This shared SIM has no resident modem on record, so there is nothing to run it on. ' +
          'Re-mark it shared from the modem’s eUICC menu.',
      };
    }
    if (!reinstallable) {
      return {
        status: 'blocked',
        blocking: true,
        holderLocationId: null,
        holderImei: null,
        message: `${consumedLabel}, and it isn’t installed on any modem — it can’t be downloaded again.`,
      };
    }
    return {
      status: 'installs_fresh',
      blocking: false,
      holderLocationId: null,
      holderImei: null,
      message: null,
    };
  }

  const holderLocationId = deviceLocationOf(io.deviceId);
  const holderImei = io.imei;
  const where = locationNameOf(holderLocationId);

  // No location picked yet — report residency, judge nothing.
  if (selectedLocationIds.length === 0) {
    return {
      status: 'runs_in_place',
      blocking: false,
      holderLocationId,
      holderImei,
      message: `Currently installed on modem ${modemLabel(holderImei)} at ${where}.`,
    };
  }

  if (holderLocationId && selectedLocationIds.includes(holderLocationId)) {
    if (selectedLocationIds.length > 1) {
      return {
        status: 'one_location_only',
        blocking: false,
        holderLocationId,
        holderImei,
        message:
          `This SIM lives on one modem at ${where} — it can only serve one location at a time. ` +
          `Runs at the other selected location${selectedLocationIds.length > 2 ? 's' : ''} will ` +
          (profile.shared || !reinstallable
            ? 'fail.'
            : 'have to relocate it, one after another.'),
      };
    }
    const holderRats = forcedRat && forcedRat !== 'auto' ? holderRatsOf?.(holderImei) ?? null : null;
    if (holderRats && forcedRat && forcedRat !== 'auto' && !holderRats.has(forcedRat)) {
      const ratLabel = FORCED_RAT_LABELS[forcedRat] || forcedRat;
      const cant = `Modem ${modemLabel(holderImei)} can’t run ${ratLabel}`;
      const policy = esimRelocationPolicy(profile as any);
      if (policy === 'never') {
        return {
          status: 'blocked',
          blocking: true,
          holderLocationId,
          holderImei,
          message: profile.shared
            ? `${cant}, and this shared SIM can’t leave it. Pick Automatic, or a different eSIM.`
            : `${cant}, and this SIM can’t be moved (${profile.locked ? 'locked to it' : 'no downloads left'}). ` +
              `Pick Automatic, or a different eSIM.`,
        };
      }
      const left = effectiveDownloadsLeft(profile as any);
      return {
        status: 'relocates',
        blocking: false,
        holderLocationId,
        holderImei,
        message: policy === 'needs_approval'
          ? `${cant}, so the run moves this SIM to a modem here that can. That uses one of its ` +
            `limited downloads (${left} left); you’ll be asked to confirm.`
          : `${cant}, so the run moves this SIM to a modem here that can (deleted there, downloaded ` +
            `again), which takes a few minutes longer.`,
      };
    }
    return {
      status: 'runs_in_place',
      blocking: false,
      holderLocationId,
      holderImei,
      message: `Runs in place on modem ${modemLabel(holderImei)} at ${where}.`,
    };
  }

  // Holder is somewhere else (or its location is unknown).
  if (profile.shared) {
    return {
      status: 'blocked',
      blocking: true,
      holderLocationId,
      holderImei,
      message:
        `This shared SIM is resident on modem ${modemLabel(holderImei)} at ${where} and can’t be moved. ` +
        `Run the test at ${where}, or pick a different eSIM.`,
    };
  }
  if (!reinstallable) {
    return {
      status: 'blocked',
      blocking: true,
      holderLocationId,
      holderImei,
      message:
        `${consumedLabel}. It is installed on modem ${modemLabel(holderImei)} at ${where} and can’t be ` +
        `relocated. Run the test at ${where}, or pick a different eSIM.`,
    };
  }
  return {
    status: 'relocates',
    blocking: false,
    holderLocationId,
    holderImei,
    message:
      `This SIM is currently installed at ${where}. The run will re-download it onto a modem ` +
      `at the selected location, which takes longer than reusing it in place.`,
  };
}
